import { defineMessages } from 'react-intl';

const messages = defineMessages({
  openCertHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.OpenCertHelperText',
    defaultMessage:
      'There is an open certification for this household. In order to make ' +
      'changes to this household, you will need to cancel or complete the ' +
      'open certification.',
  },
  // Exact, field-specific strings — not a single shared Start/End Date message.
  // Start Date is auto-populated by the existing start-date logic rather than
  // typed in, so while a member is being created there is nothing to show and
  // nothing to edit. An adult gets its date when the pending applicant is
  // approved; a minor gets it as soon as the record is created.
  addingAdultStartDateHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.AddingAdultStartDateHelperText',
    defaultMessage:
      'Start Date will be editable once the household member is approved.',
  },
  addingMinorStartDateHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.AddingMinorStartDateHelperText',
    defaultMessage:
      'Start Date will be editable once the household member is saved.',
  },
  // Mirrors the two adding Start Date messages above -- same reason, same
  // wording, and neither has to pick between the resident and application
  // vocabularies the way "Pending Applicant" does.
  addingAdultEndDateHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.AddingAdultEndDateHelperText',
    defaultMessage:
      'End Date will be editable once the household member is approved.',
  },
  addingMinorEndDateHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.AddingMinorEndDateHelperText',
    defaultMessage:
      'End Date will be editable once the household member is saved.',
  },
  // Pending Applicant banner. "Some fields": Affordable Relation and Special Needs
  // are editable here, so the blanket wording would be wrong. Interim copy.
  pendingApplicantBanner: {
    id: 'App.ResidentProfile.MiniProfileBridge.PendingApplicantBanner',
    defaultMessage:
      'Some fields in the Personal and Demographic Information sections are only editable in the application.',
  },
  pendingStartDateHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.PendingStartDateHelperText',
    defaultMessage:
      'Start Date cannot be edited while this household member is a Pending Applicant.',
  },
  pendingEndDateHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.PendingEndDateHelperText',
    defaultMessage:
      'End Date cannot be edited while this household member is a Pending Applicant.',
  },
  // Unlike Phone Number/Email Address themselves (freely editable for
  // Pending, D4), the applicant-side save path doesn't have a way to
  // persist a contact type yet -- disabled here, not left silently
  // dropped on save.
  pendingPhoneTypeHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.PendingPhoneTypeHelperText',
    defaultMessage:
      'Phone Type is not yet editable while this household member is a Pending Applicant.',
  },
  pendingEmailTypeHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.PendingEmailTypeHelperText',
    defaultMessage:
      'Email Type is not yet editable while this household member is a Pending Applicant.',
  },
  headOfHouseholdEndDateHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.HeadOfHouseholdEndDateHelperText',
    defaultMessage:
      'The current Head of Household cannot be given an End Date. Assign a new Head of Household first.',
  },
  headOfHouseholdRelationHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.HeadOfHouseholdRelationHelperText',
    defaultMessage: 'Relation cannot be edited for the Head of Household.',
  },
  headOfHouseholdAffordableRelationHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.HeadOfHouseholdAffordableRelationHelperText',
    defaultMessage:
      'Affordable Relation cannot be edited for the Head of Household.',
  },
  // Blanking a previously-saved Affordable Relation blocks Save
  // (hasClearedRequiredField), whether the user re-picked "Choose" or the
  // Type cascade cleared it for them. Nothing else on screen says so, which
  // left the Save button dead with no explanation.
  clearedAffordableRelationHelperText: {
    id: 'App.ResidentProfile.MiniProfileBridge.ClearedAffordableRelationHelperText',
    defaultMessage:
      'Affordable Relation cannot be left blank once it has been set. Choose a new Affordable Relation to save.',
  },
  // 'default' is the Relation select's own blank value, which react-hook-form
  // counts as a filled-in required field -- this is what actually blocks the
  // save once the Type cascade clears Relation back to it.
  relationRequired: {
    id: 'App.ResidentProfile.MiniProfileBridge.RelationRequired',
    defaultMessage: 'Relation is required.',
  },
  affordableRelationRequired: {
    id: 'App.ResidentProfile.MiniProfileBridge.AffordableRelationRequired',
    defaultMessage: 'Affordable Relation is required.',
  },
  startDateBeforeMoveIn: {
    id: 'App.ResidentProfile.MiniProfileBridge.StartDateBeforeMoveIn',
    defaultMessage: 'Start Date cannot be before the Household Move-In Date.',
  },
  startDateAfterEndDate: {
    id: 'App.ResidentProfile.MiniProfileBridge.StartDateAfterEndDate',
    defaultMessage: 'Start Date cannot be after the End Date.',
  },
  startDateAfterMoveOut: {
    id: 'App.ResidentProfile.MiniProfileBridge.StartDateAfterMoveOut',
    defaultMessage: 'Start Date cannot be after the Household Move-Out Date.',
  },
  startDateInFuture: {
    id: 'App.ResidentProfile.MiniProfileBridge.StartDateInFuture',
    defaultMessage: 'Start Date cannot be in the future.',
  },
  noStartDatePermission: {
    id: 'App.ResidentProfile.MiniProfileBridge.NoStartDatePermission',
    defaultMessage: 'You do not have permission to edit the Start Date.',
  },
  noEndDatePermission: {
    id: 'App.ResidentProfile.MiniProfileBridge.NoEndDatePermission',
    defaultMessage: 'You do not have permission to edit the End Date.',
  },
  endDateBeforeStartDate: {
    id: 'App.ResidentProfile.MiniProfileBridge.EndDateBeforeStartDate',
    defaultMessage: 'End Date cannot be before the Start Date.',
  },
  endDateBeforeMoveIn: {
    id: 'App.ResidentProfile.MiniProfileBridge.EndDateBeforeMoveIn',
    defaultMessage: 'End Date cannot be before the Household Move-In Date.',
  },
  endDateInFuture: {
    id: 'App.ResidentProfile.MiniProfileBridge.EndDateInFuture',
    defaultMessage: 'End Date cannot be in the future.',
  },
  endDateAfterMoveOut: {
    id: 'App.ResidentProfile.MiniProfileBridge.EndDateAfterMoveOut',
    defaultMessage: 'End Date cannot be after the Household Move-Out Date.',
  },
  dateOfBirthInFuture: {
    id: 'App.ResidentProfile.MiniProfileBridge.DateOfBirthInFuture',
    defaultMessage: 'Date of Birth cannot be in the future.',
  },
  ssnInvalid: {
    id: 'App.ResidentProfile.MiniProfileBridge.SsnInvalid',
    defaultMessage: 'SSN must be 9 digits.',
  },
  stateIdNumberMissing: {
    id: 'App.ResidentProfile.MiniProfileBridge.StateIdNumberMissing',
    defaultMessage: "Driver's License / State ID # is required with a State.",
  },
  stateIdOriginMissing: {
    id: 'App.ResidentProfile.MiniProfileBridge.StateIdOriginMissing',
    defaultMessage: "Driver's License / State ID State is required with a #.",
  },
  passportNumberMissing: {
    id: 'App.ResidentProfile.MiniProfileBridge.PassportNumberMissing',
    defaultMessage: 'Passport # is required with a Country.',
  },
  passportCountryMissing: {
    id: 'App.ResidentProfile.MiniProfileBridge.PassportCountryMissing',
    defaultMessage: 'Passport Country is required with a #.',
  },
  annualIncome: {
    id: 'App.ResidentProfile.MiniProfileBridge.AnnualIncome',
    defaultMessage: 'Annual Income',
  },
  annualIncomeInvalid: {
    id: 'App.ResidentProfile.MiniProfileBridge.AnnualIncomeInvalid',
    defaultMessage: 'Annual Income must be a valid number.',
  },
  annualIncomeMaxError: {
    id: 'App.ResidentProfile.MiniProfileBridge.AnnualIncomeMaxError',
    defaultMessage: 'Annual Income must be $999,999,999,999.99 or less.',
  },
  deleteHouseholdMember: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMember',
    defaultMessage: 'Delete',
  },
  // All three tooltips are word-for-word the messages the backend blocks the
  // delete with, so a disabled button and the toast can never disagree.
  deleteHouseholdMemberPrimaryTooltip: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMemberPrimaryTooltip',
    defaultMessage: 'You cannot delete the head of household.',
  },
  deleteHouseholdMemberOpenCertTooltip: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMemberOpenCertTooltip',
    defaultMessage:
      'There is an open certification. To delete this household member, ' +
      'you must complete or cancel the open certification.',
  },
  deleteHouseholdMemberCompletedCertTooltip: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMemberCompletedCertTooltip',
    defaultMessage:
      'This household member is on a completed certification. You cannot ' +
      'delete this household member.',
  },
  // Paired with confirmHouseholdEditWithCompletedCertifications
  // (EditApplicationHousehold/messages.js), which is shown as the banner
  // under this one. Keep the two consistent if either changes.
  addHouseholdMemberConfirmTitle: {
    id: 'App.ResidentProfile.MiniProfileBridge.AddHouseholdMemberConfirmTitle',
    defaultMessage: 'Add A Household Member',
  },
  addHouseholdMemberConfirmMessage: {
    id: 'App.ResidentProfile.MiniProfileBridge.AddHouseholdMemberConfirmMessage',
    defaultMessage:
      'You are adding a household member. Are you sure you want to proceed?',
  },
  deleteHouseholdMemberConfirmTitle: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMemberConfirmTitle',
    defaultMessage: 'Delete Household Member',
  },
  deleteHouseholdMemberConfirmBanner: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMemberConfirmBanner',
    defaultMessage: 'This Cannot Be Undone',
  },
  deleteHouseholdMemberConfirmMessage: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMemberConfirmMessage',
    defaultMessage:
      'Are you sure you want to remove this household member? This action ' +
      'is permanent — their record cannot be recovered, and they will no ' +
      'longer appear anywhere in the system.',
  },
  deleteHouseholdMemberSuccessMessage: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMemberSuccessMessage',
    defaultMessage: 'Household member deleted.',
  },
  deleteHouseholdMemberErrorMessage: {
    id: 'App.ResidentProfile.MiniProfileBridge.DeleteHouseholdMemberErrorMessage',
    defaultMessage: 'Something went wrong deleting this household member.',
  },
  // The legacy portal handlers hardcode their own success text, and two pairs
  // of actions collide there: Send Invite and Reset Password both say "Email
  // sent", and the block toggle says "Status updated" for either direction
  // because one endpoint serves both. These name the action that actually ran.
  portalInviteSent: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalInviteSent',
    defaultMessage: 'Portal invite sent.',
  },
  portalPasswordResetSent: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalPasswordResetSent',
    defaultMessage: 'Password reset email sent.',
  },
  portalAccountBlocked: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalAccountBlocked',
    defaultMessage: 'Account blocked.',
  },
  portalAccountUnblocked: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalAccountUnblocked',
    defaultMessage: 'Account unblocked.',
  },
  portalAccountReset: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalAccountReset',
    defaultMessage: 'Account reset.',
  },
  portalLoginAttemptsReset: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalLoginAttemptsReset',
    defaultMessage: 'Login attempts reset.',
  },

  // Portal Access section copy. The section's own labels, the action list,
  // the unavailable-action tooltips and the account-state banners.
  portalEmailAddressLabel: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalEmailAddressLabel',
    defaultMessage: 'Email Address',
  },
  portalClaimedLabel: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalClaimedLabel',
    defaultMessage: 'Claimed',
  },
  portalLastLoginLabel: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalLastLoginLabel',
    defaultMessage: 'Last Login',
  },
  portalInviteSentLabel: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalInviteSentLabel',
    defaultMessage: 'Invite Sent',
  },
  portalAccountManagementHeading: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalAccountManagementHeading',
    defaultMessage: 'Account Management',
  },
  portalSelectAnAction: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalSelectAnAction',
    defaultMessage: 'Select an Action',
  },
  portalSendInviteAction: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalSendInviteAction',
    defaultMessage: 'Send Portal Invite',
  },
  portalResetPasswordAction: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalResetPasswordAction',
    defaultMessage: 'Reset Password',
  },
  portalBlockAccountAction: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalBlockAccountAction',
    defaultMessage: 'Block Account',
  },
  portalUnblockAccountAction: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalUnblockAccountAction',
    defaultMessage: 'Unblock Account',
  },
  portalResetAccountAction: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalResetAccountAction',
    defaultMessage: 'Reset Account',
  },
  portalResetLoginAttemptsAction: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalResetLoginAttemptsAction',
    defaultMessage: 'Reset Login Attempts',
  },
  portalSendInviteUnavailable: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalSendInviteUnavailable',
    defaultMessage:
      'An invite can only be sent to an unblocked account with a valid, unclaimed email address.',
  },
  // Send Portal Invite tooltip while the Head of Household has not finished portal setup.
  portalSendInviteNonPrimaryUnavailable: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalSendInviteNonPrimaryUnavailable',
    defaultMessage:
      'Other household members cannot be invited until the Head of Household ' +
      'claims their portal account and confirms the household on their end.',
  },
  portalResetPasswordUnavailable: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalResetPasswordUnavailable',
    defaultMessage:
      'A password reset can only be sent to a claimed, unblocked account with an email address.',
  },
  portalToggleBlockUnavailable: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalToggleBlockUnavailable',
    defaultMessage: 'There is no portal account to block or unblock.',
  },
  portalResetAccountUnavailable: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalResetAccountUnavailable',
    defaultMessage:
      'An account can only be reset once it has been invited or claimed.',
  },
  portalResetLoginAttemptsUnavailable: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalResetLoginAttemptsUnavailable',
    defaultMessage: 'The account is not locked out.',
  },
  portalRemovedFromHousehold: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalRemovedFromHousehold',
    defaultMessage:
      'This member has been removed from the household and no longer has access to Portal.',
  },
  portalStatusUnavailable: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalStatusUnavailable',
    defaultMessage:
      'Portal account status is unavailable right now. Reopen this member to try again.',
  },
  portalAccountBlockedBanner: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalAccountBlockedBanner',
    defaultMessage:
      'Account is blocked. Residents will not be able to log in while their account is blocked.',
  },
  portalLockedOutBanner: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalLockedOutBanner',
    defaultMessage: 'Portal user is locked out, please reset.',
  },
  portalNeedEmailForReset: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalNeedEmailForReset',
    defaultMessage: 'Need valid email to send Reset Password link.',
  },
  portalNeedEmailForInvite: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalNeedEmailForInvite',
    defaultMessage:
      'Residents must have an email address to receive a Portal invite.',
  },
  portalInvalidEmail: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalInvalidEmail',
    defaultMessage: 'Invalid email.',
  },
  portalInvalidEmailWithReason: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalInvalidEmailWithReason',
    defaultMessage: 'Invalid email — {reason}',
  },
  portalEmailAlreadyInUse: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalEmailAlreadyInUse',
    defaultMessage:
      'This email is already in use for another person in your community. ' +
      'Emails must be unique for each Portal account.',
  },
  portalResetAccountConfirmTitle: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalResetAccountConfirmTitle',
    defaultMessage: 'Are you sure you want to reset the account?',
  },
  portalResetAccountConfirmBody: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalResetAccountConfirmBody',
    defaultMessage:
      'Resetting the account will require the portal user to re-claim their account.',
  },
  portalBlockConfirmTitle: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalBlockConfirmTitle',
    defaultMessage: 'Are you sure you want to block this account?',
  },
  portalBlockConfirmBody: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalBlockConfirmBody',
    defaultMessage:
      'Blocking the account will prevent the portal user from logging in.',
  },
  portalUnblockConfirmTitle: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalUnblockConfirmTitle',
    defaultMessage: 'Are you sure you want to unblock this account?',
  },
  portalUnblockConfirmBody: {
    id: 'App.ResidentProfile.MiniProfileBridge.PortalUnblockConfirmBody',
    defaultMessage:
      "Unblocking the account will restore the portal user's access.",
  },
});

export default messages;
